import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPayrolls, getPayroll, updatePayroll, previewPayroll, approvePayroll } from 'services/payrolls';
import { runAccruals } from 'services/timeOff';

const fmt     = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—';
const fmtMoney = (n) => n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STEPS = { SELECT_ORIGINAL: 1, ENTER_CORRECTIONS: 2, PREVIEW: 3 };

export default function CorrectionPayrollModal({ payroll, onClose, onApproved }) {
    const queryClient = useQueryClient();
    const [step, setStep]                     = useState(STEPS.SELECT_ORIGINAL);
    const [selectedOriginalId, setSelectedOriginalId] = useState('');
    const [corrections, setCorrections]       = useState({});
    const [selectedIds, setSelectedIds]       = useState([]);
    const [previewData, setPreviewData]       = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [approved, setApproved]             = useState(false);
    const [errorMsg, setErrorMsg]             = useState('');

    const { data: allPayrollsRaw = [] } = useQuery({ queryKey: ['payrolls'], queryFn: getPayrolls });
    const approvedPayrolls = useMemo(() => {
        const list = Array.isArray(allPayrollsRaw) ? allPayrollsRaw : (allPayrollsRaw?.data ?? []);
        return list.filter(p => p.status === 'approved' && p.id !== payroll.id);
    }, [allPayrollsRaw, payroll.id]);

    const { data: originalPayrollData, isLoading: loadingOriginalMeta } = useQuery({
        queryKey: ['payroll-detail', selectedOriginalId],
        queryFn: () => getPayroll(selectedOriginalId),
        enabled: !!selectedOriginalId,
    });

    const { data: originalPreview, isLoading: loadingOriginalPreview } = useQuery({
        queryKey: ['payroll-preview', selectedOriginalId],
        queryFn: () => previewPayroll(selectedOriginalId),
        enabled: !!selectedOriginalId,
    });

    const loadingOriginal = loadingOriginalMeta || loadingOriginalPreview;

    const originalEmployees = useMemo(() => {
        if (!originalPreview) return [];
        return originalPreview.employees || [];
    }, [originalPreview]);

    const setCorrection = (empId, value) => {
        setCorrections(prev => ({ ...prev, [empId]: value }));
    };

    const toggleEmployee = (empId) => {
        setSelectedIds(prev => prev.includes(empId) ? prev.filter(x => x !== empId) : [...prev, empId]);
    };

    const toggleAll = () => {
        if (selectedIds.length === originalEmployees.length) setSelectedIds([]);
        else setSelectedIds(originalEmployees.map(e => e.employeeId));
    };

    const selectedEmployees = originalEmployees.filter(e => selectedIds.includes(e.employeeId));

    const adjustments = useMemo(() => {
        return selectedEmployees.map(emp => {
            const original = emp.grossPay || 0;
            const corrected = parseFloat(corrections[emp.employeeId]) || original;
            return { ...emp, original, corrected, delta: corrected - original };
        });
    }, [selectedEmployees, corrections]);

    const { mutate: doUpdate, isPending: updating } = useMutation({
        mutationFn: () => {
            const employeeItems = adjustments.map(emp => ({
                employeeId: emp.employeeId,
                payments: [{
                    bonus: emp.corrected > 0 ? emp.corrected : undefined,
                    regularHours: undefined,
                }],
                adjustmentAmount: emp.delta,
                correctedGrossPay: emp.corrected,
                originalPayrollId: selectedOriginalId,
            }));
            return updatePayroll({
                payrollId: payroll.id,
                type: 'correction',
                originalPayrollId: selectedOriginalId,
                periodStart: originalPayrollData?.startDate?.slice(0, 10),
                periodEnd:   originalPayrollData?.endDate?.slice(0, 10),
                employeeItems,
                contractorItems: [],
            });
        },
        onSuccess: async () => {
            setLoadingPreview(true);
            try {
                const data = await previewPayroll(payroll.id);
                setPreviewData(data);
                setStep(STEPS.PREVIEW);
            } catch {
                setErrorMsg('Failed to load preview.');
            } finally {
                setLoadingPreview(false);
            }
        },
        onError: () => setErrorMsg('Failed to save corrections.'),
    });

    const { mutate: doApprove, isPending: approving } = useMutation({
        mutationFn: () => approvePayroll(payroll.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payrolls'] });

            if (selectedEmployees.length > 0) {
                const employees = selectedEmployees.map(emp => ({
                    employeeId: emp.employeeId,
                    hoursWorked: emp.regularHours ?? 0,
                }));
                runAccruals({
                    payPeriodStart: originalPayrollData?.startDate?.slice(0, 10) ?? payroll.startDate?.slice(0, 10),
                    payPeriodEnd: originalPayrollData?.endDate?.slice(0, 10) ?? payroll.endDate?.slice(0, 10),
                    employees,
                }).catch(() => {});
            }

            setApproved(true);
            if (onApproved) onApproved();
        },
        onError: () => setErrorMsg('Failed to approve payroll.'),
    });

    const totalDelta    = adjustments.reduce((s, e) => s + e.delta, 0);
    const hasNegative   = adjustments.some(e => e.delta < 0);

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 900, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>

                <div style={{ padding: '20px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h5 className="mb-0 fw-semibold">Correction Payroll</h5>
                        <p className="mb-0 text-muted mt-1" style={{ fontSize: 13 }}>
                            Period: {payroll.startDate?.slice(0, 10)} — {payroll.endDate?.slice(0, 10)}
                        </p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
                </div>

                <div style={{ padding: '12px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', gap: 8, fontSize: 13 }}>
                    {[
                        { n: 1, label: 'Select Original Payroll' },
                        { n: 2, label: 'Enter Corrections' },
                        { n: 3, label: 'Preview & Approve' },
                    ].map(s => (
                        <span key={s.n} style={{
                            padding: '4px 14px', borderRadius: 20, fontWeight: step === s.n ? 600 : 400,
                            background: step === s.n ? '#1B75F0' : step > s.n ? '#d4edda' : '#f0f0f0',
                            color: step === s.n ? '#fff' : step > s.n ? '#155724' : '#666',
                        }}>
                            {s.n}. {s.label}
                        </span>
                    ))}
                </div>

                <div style={{ padding: '24px' }}>
                    {errorMsg && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{errorMsg}</div>}

                    {step === STEPS.SELECT_ORIGINAL && (
                        <>
                            <p className="text-muted mb-4" style={{ fontSize: 14 }}>
                                Select the approved payroll that contains the error you want to correct.
                            </p>

                            {approvedPayrolls.length === 0 ? (
                                <div className="alert alert-warning">No approved payrolls found to correct.</div>
                            ) : (
                                <div style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}>
                                    <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr>
                                                <th style={{ width: 40 }}></th>
                                                <th>Period</th>
                                                <th>Pay Date</th>
                                                <th>Type</th>
                                                <th className="text-end">Gross Pay</th>
                                                <th className="text-end">Net Pay</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {approvedPayrolls.map(p => (
                                                <tr
                                                    key={p.id}
                                                    style={{ cursor: 'pointer', background: selectedOriginalId === p.id ? '#fff8f0' : '' }}
                                                    onClick={() => setSelectedOriginalId(p.id)}
                                                >
                                                    <td>
                                                        <input
                                                            type="radio"
                                                            checked={selectedOriginalId === p.id}
                                                            onChange={() => setSelectedOriginalId(p.id)}
                                                            onClick={e => e.stopPropagation()}
                                                        />
                                                    </td>
                                                    <td>{fmt(p.startDate)} – {fmt(p.endDate)}</td>
                                                    <td>{fmt(p.payDate)}</td>
                                                    <td style={{ textTransform: 'capitalize' }}>{(p.type || 'regular').replace('_', '-')}</td>
                                                    <td className="text-end">{fmtMoney(p.grossPay)}</td>
                                                    <td className="text-end">{fmtMoney(p.netPay)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selectedOriginalId && loadingOriginal && (
                                <p className="text-muted mt-3" style={{ fontSize: 13 }}>Loading payroll details...</p>
                            )}
                            {selectedOriginalId && !loadingOriginal && originalEmployees.length === 0 && (
                                <div className="alert alert-warning mt-3">This payroll has no employee records.</div>
                            )}

                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{ backgroundColor: '#1B75F0', color: '#fff' }}
                                    disabled={!selectedOriginalId || loadingOriginal || originalEmployees.length === 0}
                                    onClick={() => { setSelectedIds(originalEmployees.map(e => e.employeeId)); setStep(STEPS.ENTER_CORRECTIONS); }}
                                >
                                    Next: Enter Corrections →
                                </button>
                            </div>
                        </>
                    )}

                    {step === STEPS.ENTER_CORRECTIONS && (
                        <>
                            <div className="mb-3 d-flex align-items-center gap-3">
                                <span className="badge" style={{ background: '#fff3cd', color: '#856404', fontSize: 13, padding: '5px 12px', borderRadius: 20 }}>
                                    Correcting: {fmt(originalPayrollData?.startDate)} – {fmt(originalPayrollData?.endDate)}
                                </span>
                                <span className="text-muted" style={{ fontSize: 13 }}>
                                    Enter the <strong>corrected gross pay</strong> for each employee. Leave blank to keep original.
                                </span>
                            </div>

                            <div style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}>
                                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                    <thead style={{ background: '#f8f9fa' }}>
                                        <tr>
                                            <th style={{ width: 40 }}>
                                                <input type="checkbox"
                                                    checked={selectedIds.length === originalEmployees.length && originalEmployees.length > 0}
                                                    onChange={toggleAll} />
                                            </th>
                                            <th>Employee</th>
                                            <th className="text-end">Original Gross</th>
                                            <th className="text-end" style={{ width: 160 }}>Corrected Gross ($)</th>
                                            <th className="text-end">Adjustment</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {originalEmployees.map(emp => {
                                            const original  = emp.grossPay || 0;
                                            const corrected = parseFloat(corrections[emp.employeeId]) || original;
                                            const delta     = corrected - original;
                                            const selected  = selectedIds.includes(emp.employeeId);
                                            return (
                                                <tr key={emp.employeeId} style={{ opacity: selected ? 1 : 0.45 }}>
                                                    <td>
                                                        <input type="checkbox"
                                                            checked={selected}
                                                            onChange={() => toggleEmployee(emp.employeeId)} />
                                                    </td>
                                                    <td className="fw-medium">{emp.employeeFirstName} {emp.employeeLastName}</td>
                                                    <td className="text-end">{fmtMoney(original)}</td>
                                                    <td className="text-end">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            style={{ width: 140, display: 'inline-block' }}
                                                            placeholder={original.toFixed(2)}
                                                            value={corrections[emp.employeeId] ?? ''}
                                                            disabled={!selected}
                                                            onChange={e => setCorrection(emp.employeeId, e.target.value)}
                                                        />
                                                    </td>
                                                    <td className="text-end fw-semibold" style={{ color: delta > 0 ? '#198754' : delta < 0 ? '#dc3545' : '#888' }}>
                                                        {delta === 0 ? '—' : (delta > 0 ? '+' : '') + fmtMoney(delta)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-3 p-3 rounded" style={{ background: '#f8f9fa', fontSize: 13 }}>
                                <strong>Total Adjustment: </strong>
                                <span style={{ color: totalDelta > 0 ? '#198754' : totalDelta < 0 ? '#dc3545' : '#888', fontWeight: 600 }}>
                                    {totalDelta === 0 ? '$0.00' : (totalDelta > 0 ? '+' : '') + fmtMoney(totalDelta)}
                                </span>
                                {hasNegative && (
                                    <span className="ms-3 text-warning" style={{ fontSize: 12 }}>
                                        ⚠ Negative adjustments indicate overpayment recovery.
                                    </span>
                                )}
                            </div>

                            <div className="d-flex justify-content-between mt-4">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setStep(STEPS.SELECT_ORIGINAL)}>
                                    ← Back
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{ backgroundColor: '#1B75F0', color: '#fff' }}
                                    disabled={selectedIds.length === 0 || updating || loadingPreview}
                                    onClick={() => doUpdate()}
                                >
                                    {updating || loadingPreview ? 'Loading...' : 'Next: Preview →'}
                                </button>
                            </div>
                        </>
                    )}

                    {step === STEPS.PREVIEW && (
                        <>
                            {approved ? (
                                <div className="text-center py-4">
                                    <div style={{ fontSize: 48 }}>✅</div>
                                    <h5 className="fw-bold mt-3">Correction Payroll Approved!</h5>
                                    <p className="text-muted">The correction has been approved and recorded.</p>
                                    <button type="button" className="btn btn-sm" style={{ backgroundColor: '#1B75F0', color: '#fff' }} onClick={onClose}>
                                        Close
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-3">
                                        <span className="badge" style={{ background: '#fce4ec', color: '#c62828', fontSize: 13, padding: '5px 12px', borderRadius: 20 }}>
                                            Correction of: {fmt(originalPayrollData?.startDate)} – {fmt(originalPayrollData?.endDate)}
                                        </span>
                                    </div>

                                    <div className="row g-3 mb-4">
                                        {[
                                            { label: 'Employees Corrected', value: selectedEmployees.length, money: false, color: '#0d6efd' },
                                            { label: 'Total Adjustment', value: totalDelta, money: true, color: totalDelta >= 0 ? '#198754' : '#dc3545' },
                                        ].map(c => (
                                            <div key={c.label} className="col-6 col-md-3">
                                                <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', borderLeft: `4px solid ${c.color}` }}>
                                                    <p className="mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#888' }}>{c.label}</p>
                                                    <p className="mb-0 fw-bold" style={{ fontSize: 18, color: c.color }}>
                                                        {c.money ? ((c.value > 0 ? '+' : '') + fmtMoney(c.value)) : c.value}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                        {previewData && (() => {
                                            const selEmps = (previewData.employees || []).filter(e => selectedIds.includes(e.employeeId));
                                            const taxes = selEmps.reduce((s, e) => s + ((e.federalIncomeTax || 0) + (e.stateIncomeTax || 0) + (e.socialSecurityTax || 0) + (e.medicareTax || 0)), 0);
                                            const net   = selEmps.reduce((s, e) => s + (e.netPay || 0), 0);
                                            return (
                                                <>
                                                    <div className="col-6 col-md-3">
                                                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', borderLeft: '4px solid #1B75F0' }}>
                                                            <p className="mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#888' }}>Taxes on Adjustment</p>
                                                            <p className="mb-0 fw-bold" style={{ fontSize: 18, color: '#1B75F0' }}>{fmtMoney(taxes)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-6 col-md-3">
                                                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', borderLeft: '4px solid #198754' }}>
                                                            <p className="mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#888' }}>Net Adjustment</p>
                                                            <p className="mb-0 fw-bold" style={{ fontSize: 18, color: '#198754' }}>{fmtMoney(net)}</p>
                                                        </div>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>

                                    <h6 className="fw-semibold mb-2">Correction Details</h6>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr>
                                                    <th>Employee</th>
                                                    <th className="text-end">Original Gross</th>
                                                    <th className="text-end">Corrected Gross</th>
                                                    <th className="text-end">Adjustment</th>
                                                    {previewData && <th className="text-end">Est. Net Adj.</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {adjustments.map(emp => {
                                                    const preview = previewData?.employees?.find(e => e.employeeId === emp.employeeId);
                                                    return (
                                                        <tr key={emp.employeeId}>
                                                            <td className="fw-medium">{emp.employeeFirstName} {emp.employeeLastName}</td>
                                                            <td className="text-end">{fmtMoney(emp.original)}</td>
                                                            <td className="text-end">{fmtMoney(emp.corrected)}</td>
                                                            <td className="text-end fw-semibold" style={{ color: emp.delta > 0 ? '#198754' : emp.delta < 0 ? '#dc3545' : '#888' }}>
                                                                {emp.delta === 0 ? '—' : (emp.delta > 0 ? '+' : '') + fmtMoney(emp.delta)}
                                                            </td>
                                                            {previewData && (
                                                                <td className="text-end" style={{ color: '#0d6efd' }}>{fmtMoney(preview?.netPay)}</td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-between mt-4">
                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setStep(STEPS.ENTER_CORRECTIONS)}>
                                            ← Back
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            style={{ backgroundColor: '#28a745', color: '#fff' }}
                                            disabled={approving}
                                            onClick={() => doApprove()}
                                        >
                                            {approving ? 'Approving...' : '✓ Approve Correction Payroll'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}